import { z } from 'zod';
import type { FieldErrors, SubmitHandler } from 'react-hook-form';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { InputWrapper, Form, FormInput } from '../atoms/FormErrorMessage';
import type { CommonProps } from '../types/CommonProps';
import { PrimaryButton } from '../atoms';
import { FlexContainer } from '../atoms/FlexContainer';
import { FlexColumn } from '../atoms/FlexColumn';
import { FlexRow } from '../atoms/FlexRow';
import { useState } from 'react';
import { CustomDropdown } from '../atoms/CustomDropdown';
import {
  FemaleGenderIcon,
  MaleGenderIcon,
  OtherGenderIcon
} from '../atoms/icons';
import type { IconType } from 'react-icons';
import type { StyledComponent } from 'styled-components';

const GENDERS = ['Male', 'Female', 'Other', 'FOO', 'BAR', 'BAZ'];
// enum GENDERS {
//   Male,
//   Female,
//   Other
// }
type TGenderOptions = {
  readonly text: string;
  readonly icon: StyledComponent<IconType, any, any>;
};
const genderOptions: readonly TGenderOptions[] = [
  { text: 'Male', icon: MaleGenderIcon },
  { text: 'Female', icon: FemaleGenderIcon },
  { text: 'Other', icon: OtherGenderIcon }
] as const;
//TODO: Just making this up for now need to work out what validation we actually want.
const startApplicationFormSchema = z
  .object({
    firstName: z
      .string()
      .min(2, { message: 'First Name should be at least 2 characters' }),
    middleName: z
      .string()
      .min(2, {
        message: 'Middle Name should be at least 2 characters or left blank'
      })
      .or(z.literal('')),
    lastName: z
      .string()
      .min(2, { message: 'Last Name should be at least 2 characters' }),
    gender: z.string().refine((value: string) => GENDERS.includes(value), {
      message: 'Please select a gender'
    }),
    email: z
      .string()
      .min(1, { message: 'Please enter your email address.' })
      .email('Please enter a valid email address.'),
    confirmEmail: z
      .string()
      .min(1, { message: 'Please enter your email address.' })
      .email('Please enter a valid email address.'),
    mobile: z.string(),
    dob: z.coerce
      .date({
        // Workaround for bug https://github.com/colinhacks/zod/issues/1526
        errorMap: (issue, { defaultError }) => ({
          message:
            issue.code === 'invalid_date'
              ? 'Please enter a valid Date of Birth'
              : defaultError
        })
      })
      .min(new Date('1900-01-01'), 'Please enter a valid Date of Birth')
      .max(
        new Date(new Date().setFullYear(new Date().getFullYear() - 18)),
        'Customer must be at least 18 years old.'
      ),
    referralCode: z
      .string()
      .min(2, {
        message: 'Referral Code should be at least 2 characters or left blank'
      })
      .or(z.literal(''))
  })

  .superRefine(({ email, confirmEmail }, ctx) => {
    if (email !== confirmEmail) {
      ctx.addIssue({
        code: 'custom',
        message: 'Emails did not match',
        path: ['confirmEmail']
      });
    }
  });
// extracting the type from schema
type StartApplicationFormSchema = z.infer<typeof startApplicationFormSchema>;

// type TOptions = { label: string; value: string };
// const options: Array<TOptions> = [
//   { value: 'Male', label: 'Male' },
//   { value: 'Female', label: 'Female' },
//   { value: 'Other', label: 'Other' }
// ];

const StartApplicationForm = ({ className }: CommonProps) => {
  const {
    register,
    handleSubmit,
    watch,
    setFocus,
    setValue,
    trigger,
    resetField,
    formState: { errors }
  } = useForm<StartApplicationFormSchema>({
    resolver: zodResolver(startApplicationFormSchema)
  });

  const onSubmit: SubmitHandler<StartApplicationFormSchema> = (data: any) => {
    //console.log('... pretending to do search query for:', { ...data });
    // TODO: handleSubmit
  };

  const [selectedOption, setSelectedOption] = useState<string>(GENDERS[0]);

  const hasInvalidFields = (errors: FieldErrors) => {
    const invalidFields = Object.keys(errors) as Array<
      keyof StartApplicationFormSchema
    >;
    return invalidFields.length !== 0;
  };
  const getInvalidFields = (errors: FieldErrors) => {
    const invalidFields = Object.keys(errors) as Array<
      keyof StartApplicationFormSchema
    >;
    return `Please correct errors in: ${invalidFields.join(', ')}`;
  };

  const hasError = hasInvalidFields(errors);
  const { onChange, onBlur, name, ref } = register('gender');
  return (
    <Form onSubmit={handleSubmit(onSubmit)} className={className}>
      <FlexContainer>
        <FlexRow className="firstName-middleName-row">
          <FlexColumn className="left-column">
            <InputWrapper
              className="firstName-error-wrapper"
              hasError={!!errors.firstName}
              errorMessage={errors.firstName?.message}
              label={'First name'}
            >
              <FormInput
                className="firstName-input"
                id="firstName"
                type="text"
                onSubmit={() => onSubmit}
                placeholder="First Name"
                {...register('firstName')}
                hasError={!!errors.firstName}
              />
            </InputWrapper>
          </FlexColumn>
          <FlexColumn className="right-column">
            <InputWrapper
              className="middleName-error-wrapper"
              hasError={!!errors.middleName}
              errorMessage={errors.middleName?.message}
              label={'Middle Name (Optional)'}
            >
              <FormInput
                className="middleName-input"
                id="middleName"
                type="text"
                onSubmit={() => onSubmit}
                placeholder="Middle Name (Optional)"
                {...register('middleName')}
                hasError={!!errors.middleName}
              />
            </InputWrapper>
          </FlexColumn>
        </FlexRow>

        <FlexRow className="lastName-gender-row">
          <FlexColumn className="left-column">
            <InputWrapper
              className="lastName-error-wrapper"
              hasError={!!errors.lastName}
              errorMessage={errors.lastName?.message}
              label={'Last name'}
            >
              <FormInput
                className="lastName-input"
                id="lastName"
                type="text"
                onSubmit={() => onSubmit}
                placeholder="Last Name"
                {...register('lastName')}
                hasError={!!errors.lastName}
              />
            </InputWrapper>
          </FlexColumn>
          <FlexColumn className="right-column">
            <InputWrapper
              className="gender-error-wrapper"
              hasError={!!errors.gender}
              errorMessage={errors.gender?.message}
              label={'Gender'}
            >
              <CustomDropdown
                className={'gender'}
                id={'gender'}
                name={name}
                onChange={onChange}
                ref={ref}
                hasError={!!errors.gender}
                options={genderOptions}
                onSubmit={onSubmit}
                setValue={setValue}
                setFocus={setFocus}
                onBlur={onBlur}
                trigger={trigger}
                resetField={resetField}
                errors={errors.gender}
              />
            </InputWrapper>
          </FlexColumn>
        </FlexRow>
        <FlexRow className="email-confirmEmail-row">
          <FlexColumn className="left-column">
            <InputWrapper
              className="email-error-wrapper"
              hasError={!!errors.email}
              errorMessage={errors.email?.message}
              label={'Email'}
            >
              <FormInput
                className="email-input"
                id="email"
                type="email"
                onSubmit={() => onSubmit}
                placeholder="example@email.com"
                {...register('email')}
                hasError={!!errors.email}
              />
            </InputWrapper>
          </FlexColumn>
          <FlexColumn className="right-column">
            <InputWrapper
              className="confirmEmail-error-wrapper"
              hasError={!!errors.confirmEmail}
              errorMessage={errors.confirmEmail?.message}
              label={'Confirm email'}
            >
              <FormInput
                className="confirmEmail-input"
                id="confirmEmail"
                type="confirmEmail"
                onSubmit={() => onSubmit}
                placeholder="example@email.com"
                {...register('confirmEmail')}
                hasError={!!errors.confirmEmail}
              />
            </InputWrapper>
          </FlexColumn>
        </FlexRow>
        <FlexRow className="mobile-dob-row">
          <FlexColumn className="left-column">
            <InputWrapper
              className="mobile-error-wrapper"
              hasError={!!errors.mobile}
              errorMessage={errors.mobile?.message}
              label={'Mobile phone'}
            >
              <FormInput
                className="mobile-input"
                id="mobile"
                type="tel"
                onSubmit={() => onSubmit}
                placeholder="0400111222"
                {...register('mobile')}
                hasError={!!errors.mobile}
              />
            </InputWrapper>
          </FlexColumn>
          <FlexColumn className="right-column">
            <InputWrapper
              className="dob-error-wrapper"
              hasError={!!errors.dob}
              errorMessage={errors.dob?.message}
              label={'Date of birth'}
            >
              <FormInput
                className="dob-input"
                id="dob"
                type="date"
                onSubmit={() => onSubmit}
                placeholder="01/01/1990"
                {...register('dob')}
                hasError={!!errors.dob}
              />
            </InputWrapper>
          </FlexColumn>
        </FlexRow>
        <FlexRow className="submitButton-referralCode-row">
          <FlexColumn className="left-column">
            <InputWrapper
              className="referralCode-error-wrapper"
              hasError={!!errors.referralCode}
              errorMessage={errors.referralCode?.message}
              label={'Referral Code (Optional)'}
            >
              <FormInput
                className="referralCode-input"
                id="referralCode"
                type="text"
                onSubmit={() => onSubmit}
                placeholder="salesAgentId-123"
                {...register('referralCode')}
                hasError={!!errors.referralCode}
              />
            </InputWrapper>
          </FlexColumn>
          <FlexColumn className="right-column">
            <InputWrapper
              className="submit-error-wrapper"
              hasError={hasError}
              errorMessage={getInvalidFields(errors)}
            >
              {/* for debugging */}

              <PrimaryButton type="submit">Send to student</PrimaryButton>
            </InputWrapper>
          </FlexColumn>
        </FlexRow>
      </FlexContainer>
      <pre>{String(hasError)}</pre>
      <pre>{JSON.stringify(watch(), null, 2)}</pre>

      <pre>{String(errors.confirmEmail)}</pre>
    </Form>
  );
};

export { StartApplicationForm, type TGenderOptions };
